import L from "leaflet";
import { fmtScore } from "./dom";
import type { PlaceSummary } from "./types";

/** Keeps the map markers in sync with the places currently in view. */
export class MarkerLayer {
  private readonly layer: L.LayerGroup;
  private readonly markers = new Map<string, L.Marker>();
  private selected: string | null = null;

  constructor(
    private readonly map: L.Map,
    private readonly onSelect: (id: string) => void,
  ) {
    this.layer = L.layerGroup().addTo(map);
  }

  /** Replaces the visible set, keeping the selected marker even if it dropped out. */
  sync(places: PlaceSummary[]): void {
    const seen = new Set(places.map((p) => p.id));
    for (const [id, marker] of this.markers) {
      if (seen.has(id) || id === this.selected) continue;
      this.layer.removeLayer(marker);
      this.markers.delete(id);
    }
    for (const p of places) this.upsert(p);
  }

  upsert(place: PlaceSummary): void {
    const existing = this.markers.get(place.id);
    if (existing) {
      existing.setLatLng([place.lat, place.lon]);
      existing.setIcon(icon(place, place.id === this.selected));
      existing.options.title = place.name;
      return;
    }
    const marker = L.marker([place.lat, place.lon], {
      icon: icon(place, place.id === this.selected),
      title: place.name,
      keyboard: true,
      alt: place.name,
    });
    marker.on("click", () => this.onSelect(place.id));
    marker.addTo(this.layer);
    this.markers.set(place.id, marker);
  }

  select(id: string | null): void {
    const previous = this.selected;
    this.selected = id;
    for (const key of [previous, id]) {
      if (!key) continue;
      const marker = this.markers.get(key);
      const node = marker?.getElement();
      node?.classList.toggle("is-selected", key === id);
    }
  }

  /** Centers the map on a place, leaving room for the sheet on phones. */
  focus(lat: number, lon: number): void {
    const zoom = Math.max(this.map.getZoom(), 15);
    this.map.setView([lat, lon], zoom, { animate: true });
  }
}

function icon(place: PlaceSummary, selected: boolean): L.DivIcon {
  const avg = place.stats.avg_overall;
  const label = avg === null ? "?" : fmtScore(avg);
  const tone =
    avg === null ? "new" : avg < 2.5 ? "low" : avg < 3.5 ? "mid" : "high";
  return L.divIcon({
    className: `pin-wrap${selected ? " is-selected" : ""}`,
    html: `<span class="pin pin--${tone}"><span class="pin__label">${label}</span></span>`,
    iconSize: [40, 40],
    iconAnchor: [20, 38],
  });
}
